"use client";

import type { QuantitativeSimulationRegistry } from "@/lib/quantitative-simulations";

type Composition = NonNullable<QuantitativeSimulationRegistry["runs"][number]["modelComposition"]>;
const percent = (value: number) => `${(value * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
const count = (value: number, noun: string) => `${value.toLocaleString()} ${noun}${value === 1 ? "" : "s"}`;

export default function QuantitativeComposition({ value, onOpenModel }: {
  value: Composition;
  onOpenModel: (id: string) => void;
}) {
  return <section className="quant-composition" aria-label="Model contributions">
    <h3>Model contributions</h3>
    <p>{value.adaptive
      ? value.feedbackEvents
        ? `Learned from ${count(value.feedbackEvents, "completed event")} · ${count(value.feedbackContracts, "contract")}. New forecasts are evaluated separately.`
        : "Contributions begin at their registered values. They will adapt when complete event outcomes are available."
      : "These contributions stay fixed, providing a comparison for the model that adapts from outcome feedback."}</p>
    {/* Keyboard access allows the table to scroll on compact screens. */}
    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
    <div className="quant-table-scroll" role="region" aria-label="Forecast model contributions" tabIndex={0}>
      <table><thead><tr><th>Component</th><th>Registered</th><th>Current</th></tr></thead><tbody>
        {value.weights.map(w => <tr key={w.modelId}>
          <th scope="row"><button type="button" className="quant-component-link" onClick={() => onOpenModel(w.modelId)}>{w.name}<span aria-hidden="true"> →</span></button></th>
          <td>{percent(w.initialWeight)}</td><td>{percent(w.weight)}</td>
        </tr>)}
      </tbody></table>
    </div>
    <details><summary>Feedback and interpretation</summary><p>{value.scope}</p>
      {value.adaptive && <p>{count(value.waitingEvents, "event")} {value.waitingEvents === 1 ? "awaits" : "await"} complete outcomes{value.excludedEvents ? ` · ${value.excludedEvents.toLocaleString()} excluded because their evidence is unusable` : ""}.</p>}
      {value.lastFeedbackAt && <p>Latest feedback: {new Date(value.lastFeedbackAt).toLocaleString()}.</p>}
    </details>
  </section>;
}
